import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CLOSE_REASON_PLATFORMS, CLOSE_REASON_OTHERS } from '../constants/closeReasons';
import { useAuth } from '../contexts/AuthContext';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useAlert } from '../contexts/AlertContext';
import { HelperProfile, HelperSearchRequest, JobApplication, JobPosting, JobPostingSearchRequest } from '../types';
import { apiService } from '../services/api';
import { isHelperViewed, isJobViewed } from '../utils/viewedStore';
import { formatDate, formatDateTime, calculateAge } from '../utils/date';
import { HelperCard } from '../components';
import { useDataDictionaries, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictDisplayValue, resolveApiError } from '../utils/i18n';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Briefcase,
  CheckCircle2,
  Clock3,
  FileText,
  MessageCircle,
  Pencil,
  PackageX,
  Search,
  Sparkles,
  Star,
  UserRoundPen,
  Users,
} from 'lucide-react';

// 下架原因（tick box）— 與 constants/closeReasons 共用，label 經 t(labelKey) 顯示

const DEFAULT_JOB_POSTING_SEARCH_REQUEST: JobPostingSearchRequest = {
  page: 0,
  size: 10,
  sortBy: 'postedDate',
  sortDirection: 'DESC',
};

const IntentQuotaBadge: React.FC<{ employerId: number }> = ({ employerId }) => {
  const { t } = useTranslation();
  const [quota, setQuota] = React.useState<number | null>(null);
  React.useEffect(() => {
    apiService.interviewIntents.getRemainingQuota(employerId).then((res: any) => {
      if (res.success) setQuota(res.data);
    }).catch(() => {});
  }, [employerId]);
  if (quota === null) return null;
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${quota > 0 ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
      {t('homeDash.quotaLeft', { q: quota })}
    </span>
  );
};

const HomePage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { toggleBookmark, isBookmarked, toggleJobPostBookmark, isJobPostBookmarked, jobPostBookmarks, error, clearError } = useBookmarks();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [jobPostingLoading, setJobPostingLoading] = useState(false);

  const [helpers, setHelpers] = useState<HelperProfile[]>([]);
  const [helperProfile, setHelperProfile] = useState<HelperProfile | null>(null);
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [recommendedJobs, setRecommendedJobs] = useState<any[]>([]);
  const [helperApplications, setHelperApplications] = useState<JobApplication[]>([]);
  const [employerJobPostings, setEmployerJobPostings] = useState<JobPosting[]>([]);
  const [dashboardRecommended, setDashboardRecommended] = useState<any[]>([]);
  const [employerApplications, setEmployerApplications] = useState<JobApplication[]>([]);
  const [employerIntents, setEmployerIntents] = useState<any[]>([]);
  const [helperIntents, setHelperIntents] = useState<any[]>([]);
  const [bookmarkedHelpers, setBookmarkedHelpers] = useState<HelperProfile[]>([]);

  // 已收到申請的外傭資料 enrichment
  const [appProfiles, setAppProfiles] = useState<Record<number, HelperProfile>>({});
  const [appMatch, setAppMatch] = useState<Record<string, number>>({});

  // 下架對話框
  const [closingPosting, setClosingPosting] = useState<JobPosting | null>(null);
  const [foundHelper, setFoundHelper] = useState(false);
  const [closeReasons, setCloseReasons] = useState<Set<string>>(new Set());
  const [closing, setClosing] = useState(false);
  const [legalMinimumWage, setLegalMinimumWage] = useState<number | null>(null);

  const [jobPostingFilters] = useState<JobPostingSearchRequest>(DEFAULT_JOB_POSTING_SEARCH_REQUEST);
  const [jobPostingPage, setJobPostingPage] = useState(0);
  const [jobPostingTotalPages, setJobPostingTotalPages] = useState(1);
  const [hasMoreJobPostings, setHasMoreJobPostings] = useState(true);

  const parentCodes = [
    DATA_DICTIONARY_TYPES.NATIONALITY,
    DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
    DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
    DATA_DICTIONARY_TYPES.HOUSEWORK,
    DATA_DICTIONARY_TYPES.COOKING_SKILLS,
    DATA_DICTIONARY_TYPES.JOB_LOCATION,
    DATA_DICTIONARY_TYPES.JOB_TITLE,
    DATA_DICTIONARY_TYPES.WORK_TYPE,
    DATA_DICTIONARY_TYPES.EDUCATION_LEVEL,
    DATA_DICTIONARY_TYPES.CONTRACT_SITUATION,
    DATA_DICTIONARY_TYPES.LIVING_ARRANGEMENT,
    DATA_DICTIONARY_TYPES.BENEFITS,
    DATA_DICTIONARY_TYPES.OTHER_SKILLS,
    DATA_DICTIONARY_TYPES.MARITAL_STATUS,
    DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL,
  ];

  const { dictionariesByParent } = useDataDictionaries({ parentCodes });
  const nationalityDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.NATIONALITY) || [];
  const careDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE) || [];
  const houseworkDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.HOUSEWORK) || [];
  const cookingDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.COOKING_SKILLS) || [];
  const jobLocationDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.JOB_LOCATION) || [];
  const workTypeDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_TYPE) || [];
  const educationDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.EDUCATION_LEVEL) || [];
  const maritalDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.MARITAL_STATUS) || [];

  useEffect(() => {
    if (error) {
      showAlert({ type: 'error', message: error });
      clearError();
    }
  }, [error, clearError, showAlert]);

  const otherSkillsDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.OTHER_SKILLS) || [];
  const workExpSkillDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL) || [];

  const getSkillDisplayValue = useCallback((skillCode: string): string => {
    const allSkillDicts = [...careDicts, ...houseworkDicts, ...cookingDicts, ...otherSkillsDicts, ...workExpSkillDicts];
    const dict = allSkillDicts.find((item) => item.code === skillCode);
    return dict ? getDictDisplayValue(dict) : skillCode;
  }, [careDicts, cookingDicts, houseworkDicts, otherSkillsDicts, workExpSkillDicts]);

  const getDictionaryLabel = useCallback((code: string | undefined, dictionaries: { code: string }[]) => {
    if (!code) {
      return '—';
    }

    const match = dictionaries.find((item: any) => item.code === code);
    return match ? getDictDisplayValue(match as any) : code;
  }, []);

  const formatCurrency = useCallback((amount?: number, currency: string = 'HKD') => {
    if (amount === undefined || amount === null) {
      return '-';
    }

    return new Intl.NumberFormat(i18n.language?.startsWith('zh') ? 'zh-HK' : 'en-HK', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  }, [i18n.language]);

  const formatSalary = useCallback((posting: JobPosting) => {
    if (posting.salaryType === 'FIXED' && posting.fixedSalary) {
      return formatCurrency(posting.fixedSalary, posting.salaryCurrency || 'HKD');
    }
    if (posting.salaryType === 'RANGE' && (posting.minSalary || posting.maxSalary)) {
      return `${formatCurrency(posting.minSalary, posting.salaryCurrency || 'HKD')} - ${formatCurrency(posting.maxSalary, posting.salaryCurrency || 'HKD')}`;
    }
    if (posting.salaryType === 'OTHER') {
      if (posting.salaryOtherOption === 'LEGAL_MINIMUM') {
        return legalMinimumWage ? formatCurrency(legalMinimumWage) : t('jobForm.salaryLegalMin', '法定最低工資');
      }
      if (posting.salaryOtherOption === 'TO_BE_DISCUSS') return t('jobForm.salaryNegotiable', '面議');
    }
    return '—';
  }, [formatCurrency, legalMinimumWage]);

  const parseSkills = useCallback((skillsString?: string): string[] => {
    if (!skillsString) {
      return [];
    }

    return skillsString
      .split(',')
      .map((skill) => skill.trim())
      .filter(Boolean);
  }, []);

  const getStatusBadgeProps = useCallback((status?: string) => {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE':
      case 'ACCEPTED':
        return {
          variant: 'secondary' as const,
          className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700',
        };
      case 'PENDING':
      case 'DRAFT':
      case 'WITHDRAWN':
        return {
          variant: 'outline' as const,
          className: 'border-amber-500/30 bg-amber-500/10 text-amber-700',
        };
      case 'REJECTED':
      case 'CLOSED':
        return {
          variant: 'destructive' as const,
          className: 'border-red-500/20 bg-red-500/10 text-red-700',
        };
      default:
        return {
          variant: 'outline' as const,
          className: 'border-border bg-muted/40 text-muted-foreground',
        };
    }
  }, []);

  const getStatusLabel = useCallback((status?: string) => {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE':
        return t('common.active');
      case 'CLOSED':
        return t('common.closed');
      case 'DRAFT':
        return t('status.draft', 'Draft');
      case 'PENDING':
        return t('status.pending', 'Pending');
      case 'ACCEPTED':
        return t('common.accepted');
      case 'REJECTED':
        return t('status.rejected', 'Rejected');
      case 'WITHDRAWN':
          return t('status.withdrawn', 'Withdrawn');
        default:
          return status || '-';
      }
  }, [t]);

  const calculateProfileCompletion = useCallback((profile: HelperProfile | null) => {
    if (!profile) {
      return 0;
    }

    const checkpoints = [
      profile.fullName,
      profile.dateOfBirth,
      profile.nationality,
      profile.passportNumber,
      profile.maritalStatus,
      profile.educationLevel,
      profile.languageSkills,
      profile.skills,
      profile.expectedSalary,
      profile.selfIntroduction,
      profile.preferredWorkLocation,
      profile.availableStartDate,
      profile.avatarUrl,
    ];

    const completed = checkpoints.filter((item) => {
      if (typeof item === 'number') {
        return item > 0;
      }

      return Boolean(item);
    }).length;

    return Math.round((completed / checkpoints.length) * 100);
  }, []);

  const loadHelperProfile = useCallback(async (): Promise<HelperProfile | null> => {
    if (user?.role !== 'HELPER' || !user.id) {
      setHelperProfile(null);
      return null;
    }

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 5000);
      });

      const response = (await Promise.race([
        apiService.helpers.getByUserId(user.id),
        timeoutPromise,
      ])) as any;

      if (response && response.success && response.data) {
        setHelperProfile(response.data);
        return response.data;
      }

      setHelperProfile(null);
      return null;
    } catch (loadError: any) {
      setHelperProfile(null);
      // 剛註冊的外傭帳戶還沒建立檔案，屬正常情況，不應顯示錯誤提示
      const isProfileNotFound = loadError?.response?.data?.error === 'HELPER_PROFILE_NOT_FOUND';
      if (!isProfileNotFound) {
        showAlert({
          type: 'error',
          message: t('helperProfileForm.loadProfileFailed', '載入個人資料失敗'),
        });
      }
      return null;
    }
  }, [showAlert, user]);

  const loadJobPostings = useCallback(async (
    pageNum: number = 0,
    searchRequestOverride?: JobPostingSearchRequest,
    append: boolean = false,
  ) => {
    if (user?.role !== 'HELPER') {
      return;
    }

    try {
      setJobPostingLoading(true);

      const searchRequest: JobPostingSearchRequest = searchRequestOverride || {
        ...jobPostingFilters,
        page: pageNum,
      };

      const response = await apiService.jobPostings.search(searchRequest);

      if (response.success && response.data) {
        const { content, totalPages } = response.data;
        if (pageNum === 0 || !append) {
          setJobPostings(content || []);
          setJobPostingPage(0);
        } else {
          setJobPostings((previous) => [...previous, ...(content || [])]);
          setJobPostingPage(pageNum);
        }

        setJobPostingTotalPages(totalPages || 1);
        setHasMoreJobPostings(pageNum < totalPages - 1);
      }
    } catch (loadError: any) {
      showAlert({
        type: 'error',
        message: t('homeDash.loadJobsFailed', '載入職位列表失敗'),
      });
    } finally {
      setJobPostingLoading(false);
    }
  }, [jobPostingFilters, showAlert, user?.role]);

  const loadHelpers = useCallback(async () => {
    if (user?.role !== 'EMPLOYER') {
      return;
    }

    try {
      setSearchLoading(true);

      const searchRequest: HelperSearchRequest = {
        page: 0,
        size: 6,
        sortBy: 'createdAt',
        sortDirection: 'DESC',
      };

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 5000);
      });

      const response = (await Promise.race([
        apiService.helpers.search(searchRequest),
        timeoutPromise,
      ])) as any;

      if (response && response.success && response.data) {
        const formattedContent = (response.data.content || []).map((item: any) => ({
          ...item,
          expectedSalary: item.expectedSalary !== undefined && item.expectedSalary !== null
            ? typeof item.expectedSalary === 'string'
              ? parseFloat(item.expectedSalary)
              : Number(item.expectedSalary)
            : item.expectedSalary,
        }));

        setHelpers(formattedContent);
      } else {
        setHelpers([]);
      }
    } catch (loadError: any) {
      setHelpers([]);
      showAlert({
        type: 'error',
        message: loadError.response?.data?.message || t('homeDash.loadCandidatesFailed', '載入候選人資料失敗'),
      });
    } finally {
      setSearchLoading(false);
    }
  }, [showAlert, user?.role]);

  const loadEmployerJobPostings = useCallback(async () => {
    if (user?.role !== 'EMPLOYER' || !user.id) {
      return;
    }

    try {
      const response = await apiService.jobPostings.getByEmployer(user.id, 0, 20);
      if (response.success && response.data) {
        setEmployerJobPostings(response.data.content || []);
      } else {
        setEmployerJobPostings([]);
      }
    } catch (loadError) {
      setEmployerJobPostings([]);
      showAlert({
        type: 'error',
        message: t('home.loadEmployerJobsFailed', '載入職位資料失敗'),
      });
    }
  }, [showAlert, t, user]);

  const loadEmployerApplications = useCallback(async () => {
    if (user?.role !== 'EMPLOYER' || !user.id) {
      return;
    }

    try {
      const response = await apiService.jobApplications.getByEmployer(user.id);
      if (response.success && response.data) {
        setEmployerApplications(response.data);
      } else {
        setEmployerApplications([]);
      }
    } catch (loadError) {
      setEmployerApplications([]);
      showAlert({
        type: 'error',
        message: t('home.loadEmployerApplicationsFailed', '載入申請資料失敗'),
      });
    }
  }, [showAlert, t, user]);

  const loadBookmarkedHelpers = useCallback(async () => {
    if (user?.role !== 'EMPLOYER') {
      return;
    }

    try {
      const response = await apiService.candidateBookmarks.getMyBookmarks();
      if (response.success && response.data) {
        setBookmarkedHelpers(response.data);
      } else {
        setBookmarkedHelpers([]);
      }
    } catch (loadError) {
      setBookmarkedHelpers([]);
      showAlert({
        type: 'error',
        message: t('home.loadBookmarksFailed', '載入收藏外傭失敗'),
      });
    }
  }, [showAlert, t, user?.role]);

  const loadHelperApplications = useCallback(async (helperId?: number) => {
    if (user?.role !== 'HELPER' || !helperId) {
      setHelperApplications([]);
      return;
    }

    try {
      const response = await apiService.jobApplications.getByHelper(helperId);
      if (response.success && response.data) {
        setHelperApplications(response.data);
      } else {
        setHelperApplications([]);
      }
    } catch (loadError) {
      setHelperApplications([]);
      showAlert({
        type: 'error',
        message: t('home.loadApplicationsFailed', '載入申請進度失敗'),
      });
    }
  }, [showAlert, t, user?.role]);

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      if (!user) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
      }

      if (user.role === 'HELPER') {
        const profile = await loadHelperProfile();
        await loadHelperApplications(user.id);
        apiService.interviewIntents.getByHelper(user.id)
          .then((res: any) => { if (res.success && res.data) setHelperIntents(res.data); })
          .catch(() => {});
        if (profile?.id) {
          apiService.helpers.getRecommendedJobs(profile.id, 50).then((res: any) => {
            if (res.success && res.data) setRecommendedJobs(res.data.filter((m: any) => m.matchPercent >= 80));
          }).catch(() => {});
        }
      } else if (user.role === 'EMPLOYER') {
        await Promise.all([
          loadHelpers(),
          loadEmployerJobPostings(),
          loadEmployerApplications(),
          loadBookmarkedHelpers(),
          apiService.jobPostings.getLegalMinimumWage().then((r: any) => {
            if (r?.success && r.data?.amount) setLegalMinimumWage(r.data.amount);
          }).catch(() => {}),
        ]);
      }

      if (isMounted) {
        setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [
    loadBookmarkedHelpers,
    loadEmployerApplications,
    loadEmployerJobPostings,
    loadHelperApplications,
    loadHelperProfile,
    loadHelpers,
    loadJobPostings,
    user,
  ]);

  // 每次導航回 dashboard 時重新載入申請資料（例如從 JobPostingDetailPage 表達興趣後返回）
  useEffect(() => {
    if (user?.role === 'HELPER' && user?.id) {
      loadHelperApplications(user.id);
    }
  }, [location.key]);

  const handleLoadMoreJobPostings = useCallback(() => {
    if (hasMoreJobPostings && !jobPostingLoading) {
      const nextPage = jobPostingPage + 1;
      loadJobPostings(nextPage, undefined, true);
    }
  }, [hasMoreJobPostings, jobPostingLoading, jobPostingPage, loadJobPostings]);

  const handleViewDetail = useCallback((helper: HelperProfile) => {
    navigate(`/helper/${helper.id}`);
  }, [navigate]);

  // Load dashboard recommendations from first active job posting
  useEffect(() => {
    if (user?.role !== 'EMPLOYER' || employerJobPostings.length === 0) return;
    const activePosting = employerJobPostings.find((p) => p.status === 'ACTIVE') ?? employerJobPostings[0];
    apiService.jobPostings.getRecommendedHelpers(activePosting.id, 10).then((res: any) => {
      if (res.success && res.data) {
        setDashboardRecommended(res.data.filter((m: any) => m.matchPercent >= 80));
      }
    }).catch(() => {});
  }, [employerJobPostings, user?.role]);

  // 已收到的申請：載入外傭完整資料 + 匹配度
  useEffect(() => {
    if (user?.role !== 'EMPLOYER' || employerApplications.length === 0) return;
    const apps = employerApplications.slice(0, 6);

    // 外傭完整資料（年齡、婚姻、子女、學歷、經驗）
    const profileIds = Array.from(new Set(apps.map((a: any) => a.helperProfileId).filter(Boolean)));
    Promise.all(profileIds.map((pid) => apiService.helpers.getById(pid).catch(() => null)))
      .then((results) => {
        const map: Record<number, HelperProfile> = {};
        results.forEach((res: any, i) => {
          if (res?.success && res.data) map[profileIds[i] as number] = res.data;
        });
        setAppProfiles(map);
      });

    // 匹配度：每個相關職位取推薦名單，建立 (職位_外傭) -> 匹配度
    const postingIds = Array.from(new Set(apps.map((a: any) => a.jobPostingId).filter(Boolean)));
    Promise.all(postingIds.map((jid) => apiService.jobPostings.getRecommendedHelpers(jid, 500).catch(() => null)))
      .then((results) => {
        const map: Record<string, number> = {};
        results.forEach((res: any, i) => {
          if (res?.success && res.data) {
            res.data.forEach((m: any) => {
              map[`${postingIds[i]}_${m.helper.id}`] = m.matchPercent;
            });
          }
        });
        setAppMatch(map);
      });
  }, [employerApplications, user?.role]);

  // Load employer intents
  useEffect(() => {
    if (user?.role !== 'EMPLOYER' || !user.id) return;
    apiService.interviewIntents.getByEmployer(user.id).then((res: any) => {
      if (res.success) setEmployerIntents(res.data || []);
    }).catch(() => {});
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const profileCompletion = useMemo(() => calculateProfileCompletion(helperProfile), [calculateProfileCompletion, helperProfile]);

  const openCloseDialog = (posting: JobPosting) => {
    setClosingPosting(posting);
    setFoundHelper(false);
    setCloseReasons(new Set());
  };

  const toggleReason = (code: string) => {
    setCloseReasons((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const submitClose = async () => {
    if (!closingPosting) return;
    const reasons: string[] = [];
    if (foundHelper) {
      reasons.push('FOUND_HELPER');
      CLOSE_REASON_PLATFORMS.forEach((p) => closeReasons.has(p.code) && reasons.push(p.code));
    }
    CLOSE_REASON_OTHERS.forEach((r) => closeReasons.has(r.code) && reasons.push(r.code));

    try {
      setClosing(true);
      const res = await apiService.jobPostings.updateStatus(closingPosting.id, 'CLOSED', reasons.join(','));
      if (res.success) {
        setEmployerJobPostings((prev) =>
          prev.map((p) => (p.id === closingPosting.id ? { ...p, status: 'CLOSED' } : p)),
        );
        showAlert({ type: 'success', message: t('homeDash.jobClosed', '職位已下架') });
        setClosingPosting(null);
      } else {
        showAlert({ type: 'error', message: resolveApiError(res.errorCode, res.message, t) });
      }
    } catch (err: any) {
      showAlert({ type: 'error', message: err?.response?.data?.message || t('homeDash.closeFailedRetry', '下架失敗，請稍後再試') });
    } finally {
      setClosing(false);
    }
  };

  const renderSectionEmpty = (message: string) => (
    <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );

  const renderStatusBadge = (status?: string) => {
    const badge = getStatusBadgeProps(status);

    return (
      <Badge variant={badge.variant} className={badge.className}>
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const renderEmployerDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-xl">{t('jobPostings.my.title', '管理職位')}（{employerJobPostings.length}）</CardTitle>
                <p className="text-sm text-muted-foreground">{t('jobPostings.my.subtitle', '管理及追蹤已發佈的職位')}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/job-postings/new')}>
                {t('jobPostings.my.addJob', '+ 新增職位')}
              </Button>
            </CardHeader>
            <CardContent>
              {employerJobPostings.length === 0 ? renderSectionEmpty(t('home.noJobPosts')) : (
                <Tabs defaultValue="all">
                  <TabsList className="mb-4 w-full overflow-x-auto flex-wrap h-auto gap-1">
                    <TabsTrigger value="all">{t('jobPostings.my.tabs.all', '全部')}（{employerJobPostings.length}）</TabsTrigger>
                    <TabsTrigger value="active">{t('common.active')}（{employerJobPostings.filter(p => p.status === 'ACTIVE').length}）</TabsTrigger>
                    <TabsTrigger value="closed">{t('common.closed')}（{employerJobPostings.filter(p => p.status === 'CLOSED').length}）</TabsTrigger>
                    <TabsTrigger value="draft">{t('jobPostings.my.tabs.draft', '草稿')}（{employerJobPostings.filter(p => p.status === 'DRAFT').length}）</TabsTrigger>
                  </TabsList>
                  {(['all', 'active', 'closed', 'draft'] as const).map((tab) => {
                    const filtered = tab === 'all' ? employerJobPostings : employerJobPostings.filter(p => p.status === tab.toUpperCase());
                    const visible = filtered.slice(0, 5);
                    const hasMore = filtered.length > 5;
                    return (
                      <TabsContent key={tab} value={tab} className="space-y-3">
                        {filtered.length === 0 ? renderSectionEmpty(t('jobPostings.my.noJobsInCategory', '此分類暫無職位')) : (
                          <>
                            {visible.map((posting) => (
                              <div
                                key={posting.id}
                                className="flex items-start justify-between gap-4 rounded-2xl border border-border/70 bg-background px-4 py-4 transition hover:border-primary/40 hover:bg-primary/5"
                              >
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => navigate(`/job-postings/${posting.id}/detail`)}
                                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/job-postings/${posting.id}/detail`)}
                                  className="min-w-0 flex-1 cursor-pointer space-y-2 text-left"
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="font-medium text-foreground break-words">{posting.headline || posting.title}</p>
                                    {renderStatusBadge(posting.status)}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                    <span>{getDictionaryLabel(posting.location, jobLocationDicts)}</span>
                                    <span>•</span>
                                    <span>{getDictionaryLabel(posting.workType, workTypeDicts)}</span>
                                    <span>•</span>
                                    <span>{formatDate(posting.postedDate)}</span>
                                  </div>
                                  <p className="line-clamp-2 max-w-2xl break-words text-sm text-muted-foreground">{posting.description}</p>
                                  <p className="text-sm font-medium text-foreground">{formatSalary(posting)}</p>
                                </div>
                                <div className="flex shrink-0 flex-col gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-1.5"
                                    onClick={() => navigate(`/job-postings/${posting.id}/edit`)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                    {t('jobPostings.my.actions.edit', '修改')}
                                  </Button>
                                  {posting.status !== 'CLOSED' && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-1.5 text-destructive hover:text-destructive"
                                      onClick={() => openCloseDialog(posting)}
                                    >
                                      <PackageX className="h-3.5 w-3.5" />
                                      {t('jobPostings.my.actions.close', '下架')}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                            {hasMore && (
                              <Button variant="outline" className="w-full" onClick={() => navigate('/my-job-postings', { state: { tab } })}>
                                {t('homeDash.viewMoreCount', { n: filtered.length - 5 })}
                              </Button>
                            )}
                          </>
                        )}
                      </TabsContent>
                    );
                  })}
                </Tabs>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-xl">{t('homeDash.applicationsReceived', '已收到的申請')}（{employerApplications.length}）</CardTitle>
                <p className="text-sm text-muted-foreground">{t('home.reviewApplicantsHint', 'Review helper interest and respond quickly.')}</p>
              </div>
            </CardHeader>
            <CardContent>
              {employerApplications.length === 0 ? (
                renderSectionEmpty(t('home.noApplications', 'No applications yet.'))
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {employerApplications.slice(0, 6).map((application: any) => {
                    const profile = appProfiles[application.helperProfileId];
                    const age = calculateAge(profile?.dateOfBirth);
                    const match = appMatch[`${application.jobPostingId}_${application.helperProfileId}`];
                    const isAccepted = application.status === 'ACCEPTED';
                    const isRejected = application.status === 'REJECTED';
                    const isPending = application.status === 'PENDING';
                    return (
                      <div
                        key={application.id}
                        className="relative flex flex-col gap-3 rounded-2xl border border-border/70 bg-background p-4 text-left"
                      >
                        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                          {typeof match === 'number' && match >= 93 && (
                            <Badge className="bg-amber-500 text-white border-0 text-[11px]">⚡ {t('homeDash.highMatch', '高匹配')}</Badge>
                          )}
                          {isHelperViewed(application.helperProfileId) && (
                            <Badge variant="outline" className="text-[11px] text-muted-foreground">{t('homeDash.viewed', '已瀏覽')}</Badge>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (!user?.isVip) { navigate('/subscription'); return; }
                            if (application.helperProfileId) navigate(`/helper/${application.helperProfileId}`);
                          }}
                          className="flex items-center gap-3 text-left"
                        >
                          <Avatar className="h-12 w-12 shrink-0">
                            <AvatarImage src={application.helperAvatarUrl || profile?.avatarUrl} alt={application.helperName} />
                            <AvatarFallback>{application.helperName?.[0] ?? '?'}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2 pr-16">
                              <p className="truncate font-semibold text-foreground">{application.helperName}</p>
                              {typeof match === 'number' && (
                                <Badge variant={match >= 90 ? 'default' : 'secondary'} className="shrink-0">{match}%</Badge>
                              )}
                            </div>
                            <p className="truncate text-xs text-muted-foreground">{application.jobPostingTitle}</p>
                          </div>
                        </button>
                        <div className="flex flex-wrap gap-1.5 text-xs">
                          {age !== null && <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{age} {t('helperDetail.yrsOld', '歲')}</span>}
                          {profile?.maritalStatus && <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{getDictionaryLabel(profile.maritalStatus, maritalDicts)}</span>}
                          {profile?.numberOfChildren != null && <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{t('homeDash.nChildren', { n: profile.numberOfChildren })}</span>}
                          {profile?.educationLevel && <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{getDictionaryLabel(profile.educationLevel, educationDicts)}</span>}
                          {profile?.experienceYears != null && <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{t('homeDash.yearsExp', { n: profile.experienceYears })}</span>}
                        </div>
                        {/* Action buttons */}
                        <div className="flex gap-2 pt-1 border-t border-border/50">
                          {isPending && (
                            <>
                              <Button size="sm" className="flex-1 h-8 text-xs" onClick={async () => {
                                await apiService.jobApplications.updateStatus(application.id, 'ACCEPTED');
                                setEmployerApplications(prev => prev.map((a: any) => a.id === application.id ? { ...a, status: 'ACCEPTED' } : a));
                              }}>{t('homeDash.accept', '接受')}</Button>
                              <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={async () => {
                                await apiService.jobApplications.updateStatus(application.id, 'REJECTED');
                                setEmployerApplications(prev => prev.map((a: any) => a.id === application.id ? { ...a, status: 'REJECTED' } : a));
                              }}>{t('homeDash.decline', '婉拒')}</Button>
                            </>
                          )}
                          {isAccepted && (
                            <Button size="sm" className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700" onClick={() => {
                              if (!user?.isVip) { navigate('/subscription'); return; }
                              const phone = application.helperPhone?.replace(/\D/g, '');
                              if (phone) window.open(`https://wa.me/${phone}`, '_blank');
                            }}>{t('helperDetail.contactWhatsapp', '聯絡外傭（WhatsApp）')}</Button>
                          )}
                          {isRejected && (
                            <span className="text-xs text-muted-foreground py-1">{t('homeDash.declined', '已婉拒')}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Interview Intents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-xl">{t('homeDash.interviewIntents', '面試意向')}（{employerIntents.length}）</CardTitle>
                <p className="text-sm text-muted-foreground">{t('homeDash.intentsSubtitle', '已發送的面試意向及外傭回覆狀態（7天內有效）')}</p>
              </div>
              <IntentQuotaBadge employerId={user!.id!} />
            </CardHeader>
            <CardContent>
              {employerIntents.length === 0 ? (
                renderSectionEmpty(t('homeDash.noIntents', '尚未發送任何面試意向。'))
              ) : (
                <>
                  <div className="overflow-x-auto rounded-2xl border border-border/70">
                    <div className="min-w-[720px]">
                      <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1.1fr_1.1fr] bg-muted/40 px-4 py-3 text-xs font-semibold text-muted-foreground">
                        <span>{t('homeDash.helperCol', '外傭')}</span>
                        <span className="text-center">{t('homeDash.repliedCol', '是否已回覆')}</span>
                        <span className="text-center">{t('homeDash.replyContentCol', '回覆內容')}</span>
                        <span>{t('homeDash.sentDateCol', '發出日期')}</span>
                        <span>{t('homeDash.lastUpdatedCol', '最後更新時間')}</span>
                      </div>
                      {employerIntents.slice(0, 10).map((intent: any) => {
                        const replied = intent.status !== 'PENDING';
                        const accepted = intent.status === 'ACCEPTED';
                        const handleContact = (e: React.MouseEvent) => {
                          e.stopPropagation();
                          if (intent.helperProfileId) navigate(`/helper/${intent.helperProfileId}`);
                        };
                        return (
                          <div
                            key={intent.id}
                            className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1fr_1.2fr] items-center gap-2 border-t border-border/60 px-4 py-3 text-sm cursor-pointer hover:bg-muted/30 transition-colors"
                            onClick={() => intent.helperProfileId && navigate(`/helper/${intent.helperProfileId}`)}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <Avatar className="h-8 w-8 shrink-0">
                                <AvatarImage src={intent.helperAvatarUrl} alt={intent.helperName} />
                                <AvatarFallback>{intent.helperName?.[0] ?? '?'}</AvatarFallback>
                              </Avatar>
                              <span className="truncate font-medium text-foreground">{intent.helperName || '—'}</span>
                            </span>
                            <span className={`text-center font-medium ${replied ? 'text-green-600' : 'text-muted-foreground'}`}>{replied ? 'Y' : 'N'}</span>
                            <span className={`text-center font-medium ${accepted ? 'text-green-600' : replied ? 'text-red-500' : 'text-muted-foreground'}`}>
                              {replied ? (accepted ? 'Y' : 'N') : '—'}
                            </span>
                            <span className="text-muted-foreground text-xs">{formatDateTime(intent.createdAt)}</span>
                            {accepted ? (
                              <button
                                type="button"
                                onClick={handleContact}
                                className="flex items-center gap-1.5 justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                              >
                                <MessageCircle className="h-3.5 w-3.5" />
                                {t('helperDetail.contactHelper', '聯絡外傭')}
                              </button>
                            ) : (
                              <span className="text-muted-foreground text-xs">{formatDateTime(intent.updatedAt || intent.respondedAt)}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {employerIntents.length > 10 && (
                    <Button variant="outline" className="mt-4 w-full" onClick={() => navigate('/interview-intents')}>
                      {t('homeDash.viewMoreCount', { n: employerIntents.length })}
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xl">{t('home.bookmarkedHelpers', 'Bookmarked helpers')}（{bookmarkedHelpers.length}）</CardTitle>
              {bookmarkedHelpers.length > 3 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/my-candidates')}>{t('homeDash.viewMore', '查看更多')}</Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {bookmarkedHelpers.length === 0
                ? renderSectionEmpty(t('home.noBookmarkedHelpers', 'No bookmarked helpers yet.'))
                : (
                  <>
                    {bookmarkedHelpers.slice(0, 3).map((helper) => (
                      <button
                        key={helper.id}
                        type="button"
                        onClick={() => handleViewDetail(helper)}
                        className="flex w-full items-center justify-between rounded-2xl border border-border/70 px-4 py-3 text-left transition hover:border-primary/40 hover:bg-primary/5"
                      >
                        <div>
                          <p className="font-medium text-foreground">{helper.fullName}</p>
                          <p className="text-sm text-muted-foreground">{getDictionaryLabel(helper.nationality, nationalityDicts)}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isHelperViewed(helper.id) && (
                            <Badge variant="outline" className="text-[11px] text-muted-foreground">{t('homeDash.viewed', '已瀏覽')}</Badge>
                          )}
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        </div>
                      </button>
                    ))}
                  </>
                )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{t('home.recommendedHelpers', '推薦外傭')}（{dashboardRecommended.length}）</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {dashboardRecommended.length === 0
                ? renderSectionEmpty(t('homeDash.noMatchingHelpers', '暫無符合條件的外傭'))
                : dashboardRecommended.map((m) => (
                    <button
                      key={m.helper.id}
                      type="button"
                      onClick={() => navigate(`/helper/${m.helper.id}`)}
                      className="flex w-full items-center gap-3 rounded-xl border border-border/70 px-3 py-2 text-left transition hover:border-primary/40 hover:bg-primary/5"
                    >
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarImage src={m.helper.avatarUrl} alt={m.helper.fullName} />
                        <AvatarFallback>{m.helper.fullName?.[0] ?? '?'}</AvatarFallback>
                      </Avatar>
                      <p className="min-w-0 flex-1 truncate font-medium text-foreground">{m.helper.fullName}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {m.matchPercent >= 93 && (
                          <Badge className="bg-amber-500 text-white border-0 text-[11px]">⚡ {t('homeDash.highMatch', '高匹配')}</Badge>
                        )}
                        {isHelperViewed(m.helper.id) && (
                          <Badge variant="outline" className="text-[11px] text-muted-foreground">{t('homeDash.viewed', '已瀏覽')}</Badge>
                        )}
                        <Badge variant={m.matchPercent >= 90 ? 'default' : 'secondary'} className="shrink-0">
                          {m.matchPercent}%
                        </Badge>
                      </div>
                    </button>
                  ))
              }
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 下架原因對話框 */}
      <Dialog open={!!closingPosting} onOpenChange={(open) => !open && setClosingPosting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('hireCheck.closeJobTitle', '下架職位')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t('homeDash.closeReasonPrompt', { title: closingPosting?.headline || closingPosting?.title })}
            </p>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={foundHelper} onCheckedChange={(v) => setFoundHelper(!!v)} />
                {t('homeDash.foundSomeone', '已找到人')}
              </label>
              {foundHelper && (
                <div className="ml-6 space-y-2 rounded-xl border border-border bg-muted/30 p-3">
                  <p className="text-xs text-muted-foreground">{t('homeDash.whichPlatform', '在哪個平台？')}</p>
                  {CLOSE_REASON_PLATFORMS.map((p) => (
                    <label key={p.code} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={closeReasons.has(p.code)} onCheckedChange={() => toggleReason(p.code)} />
                      {t(p.labelKey, p.label)}
                    </label>
                  ))}
                </div>
              )}
              {CLOSE_REASON_OTHERS.map((r) => (
                <label key={r.code} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={closeReasons.has(r.code)} onCheckedChange={() => toggleReason(r.code)} />
                  {t(r.labelKey, r.label)}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosingPosting(null)} disabled={closing}>
              {t('common.cancel', '取消')}
            </Button>
            <Button onClick={submitClose} disabled={closing}>
              {closing ? t('hireCheck.processing', '處理中...') : t('hireCheck.confirmClose', '確認下架')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderHelperDashboard = () => (
    <div className="space-y-6">
      {!helperProfile && (
        <Card className="border-primary/20 bg-primary/5 shadow-sm">
          <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <p className="text-lg font-semibold text-foreground">{t('home.profileNotCreated')}</p>
              <p className="text-sm text-muted-foreground">{t('home.completeProfilePrompt', 'Create your profile to improve visibility and application success.')}</p>
            </div>
            <Button onClick={() => navigate('/helper/profile/new')}>{t('home.createProfile')}</Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xl">{t('home.profileCompletion', 'Profile completion')}</CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => navigate(helperProfile ? `/helper/profile/${helperProfile.id}/edit` : '/helper/profile/new')}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('common.editProfile')}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-4xl font-semibold text-foreground">{profileCompletion}%</p>
                  <p className="text-sm text-muted-foreground">{t('home.profileCompletionHint', 'A complete profile helps employers understand your strengths faster.')}</p>
                </div>
                {!helperProfile?.lookingForJob && (
                  <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-700">
                    <Clock3 className="mr-1 h-3.5 w-3.5" />
                    {t('home.profileIncomplete', 'Needs attention')}
                  </Badge>
                )}
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${profileCompletion}%` }} />
              </div>
              {helperProfile && (
                <>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t('home.location', 'Location')}</p>
                      <p className="mt-2 font-medium text-foreground">{getDictionaryLabel(helperProfile.preferredWorkLocation, jobLocationDicts)}</p>
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t('home.expectedSalary', 'Expected salary')}</p>
                      <p className="mt-2 font-medium text-foreground">{formatCurrency(helperProfile.expectedSalary, helperProfile.expectedSalaryCurrency || 'HKD')}</p>
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t('common.availableFrom')}</p>
                      <p className="mt-2 font-medium text-foreground">
                        {helperProfile.availableStartDate
                          ? formatDate(helperProfile.availableStartDate)
                          : helperProfile.lookingForJob
                            ? t('common.anytime', 'ANYTIME')
                            : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-border/50 pt-4">
                    <p className="text-sm font-medium text-muted-foreground mb-3">{t('homeDash.profileHighlights', 'Profile highlights')}</p>
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-2 rounded-2xl border border-border/70 bg-muted/20 px-3 py-2 text-sm">
                        <Users className="h-4 w-4 text-primary shrink-0" />
                        <span>{getDictionaryLabel(helperProfile.nationality, nationalityDicts)}</span>
                      </div>
                      <div className="flex items-center gap-2 rounded-2xl border border-border/70 bg-muted/20 px-3 py-2 text-sm">
                        <Sparkles className="h-4 w-4 text-primary shrink-0" />
                        <span>
                          {parseSkills(helperProfile.skills)
                            .slice(0, 3)
                            .map((skill) => getSkillDisplayValue(skill))
                            .join(' · ') || t('home.addMoreSkills', 'Add more skills to strengthen your profile.')}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-xl">{t('homeDash.bestMatchJobs', '最匹配職位')}（{recommendedJobs.length}）</CardTitle>
                <p className="text-sm text-muted-foreground">{t('homeDash.bestMatchSubtitle', '根據您的技能及條件，匹配度 80% 或以上的職位')}</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {recommendedJobs.length === 0 ? (
                renderSectionEmpty(t('homeDash.noMatchingJobs', '暫無符合條件的職位，請完善個人資料以提升匹配度'))
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    {recommendedJobs.slice(0, 4).map((m: any) => {
                      const posting = m.jobPosting;
                      return (
                        <Card
                          key={posting.id}
                          className="border-border/70 bg-background shadow-none transition hover:border-primary/40 hover:bg-primary/5 flex flex-col cursor-pointer"
                          onClick={() => navigate(`/job-postings/${posting.id}/detail`)}
                        >
                          <CardContent className="flex flex-col flex-1 gap-3 p-5">
                            <div className="flex items-start justify-between gap-3">
                              <div className="space-y-1.5 min-w-0 flex-1">
                                <p className="font-semibold text-foreground">{posting.headline || posting.title}</p>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                  <span>{getDictionaryLabel(posting.location, jobLocationDicts)}</span>
                                  <span>•</span>
                                  <span>{getDictionaryLabel(posting.workType, workTypeDicts)}</span>
                                </div>
                                <p className="text-sm font-medium text-foreground">{formatSalary(posting)}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                {isJobViewed(posting.id) && (
                                  <Badge variant="outline" className="text-[11px] text-muted-foreground">{t('homeDash.viewed', '已瀏覽')}</Badge>
                                )}
                                <Badge variant={m.matchPercent >= 90 ? 'default' : 'secondary'}>
                                  {m.matchPercent}%
                                </Badge>
                              </div>
                            </div>
                            {m.matchedReasons?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {m.matchedReasons.slice(0, 2).map((r: string, i: number) => (
                                  <span key={i} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{r}</span>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  {recommendedJobs.length > 4 && (
                    <Button variant="outline" className="w-full" onClick={() => navigate('/helper/recommended-jobs')}>
                      {t('homeDash.viewMoreCount', { n: recommendedJobs.length - 4 })}
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xl">{t('home.applicationStatus', '申請狀態')}（{helperApplications.length}）</CardTitle>
              {helperApplications.length > 3 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/my-applications')}>{t('homeDash.viewMore', '查看更多')}</Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {helperApplications.length === 0
                ? renderSectionEmpty(t('home.noApplications', 'No applications yet.'))
                : (
                  <>
                    {helperApplications.slice(0, 3).map((application) => {
                      const replyText = application.status === 'ACCEPTED' ? t('homeDash.accept', '接受') : application.status === 'REJECTED' ? t('homeDash.decline', '婉拒') : t('homeDash.pendingReply', '待回覆');
                      const fmtDt = (iso?: string) => {
                        if (!iso) return '—';
                        const d = new Date(iso);
                        const p = (n: number) => String(n).padStart(2, '0');
                        return `${d.getFullYear()}/${p(d.getMonth()+1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
                      };
                      return (
                        <div key={application.id} className="rounded-2xl border border-border/70 px-4 py-3 space-y-2">
                          <button
                            type="button"
                            className="font-medium text-foreground text-sm leading-snug text-left hover:underline w-full"
                            onClick={() => navigate(`/job-postings/${application.jobPostingId}/detail`)}
                          >{application.jobPostingTitle}</button>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span>{t('homeDash.replyPrefix', { v: replyText })}</span>
                            <span>{t('homeDash.sentPrefix', { v: fmtDt(application.appliedDate || application.createdAt) })}</span>
                            <span className="col-span-2">{t('homeDash.updatedPrefix', { v: fmtDt(application.updatedAt) })}</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xl">{t('homeDash.receivedInvitations', 'Received Invitations')}（{helperIntents.length}）</CardTitle>
                {helperIntents.filter((i: any) => i.status === 'PENDING').length > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                    {helperIntents.filter((i: any) => i.status === 'PENDING').length}
                  </span>
                )}
              </div>
              {helperIntents.length > 3 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/helper/invitations')}>{t('homeDash.viewMore', 'View more')}</Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {helperIntents.length === 0
                ? renderSectionEmpty(t('homeDash.noInvitations', 'No interview invitations yet.'))
                : helperIntents.slice(0, 3).map((intent: any) => (
                  <div key={intent.id} className="rounded-2xl border border-border/70 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        className="font-medium text-foreground text-sm leading-snug text-left hover:underline"
                        onClick={() => navigate(`/job-postings?keyword=${encodeURIComponent(intent.employerName || '')}`)}
                      >
                        {intent.employerName || t('helperIntents.unknownEmployer', 'Unknown employer')}
                      </button>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        intent.status === 'ACCEPTED' ? 'bg-green-100 text-green-700' :
                        intent.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {intent.status === 'ACCEPTED' ? t('helperIntents.accepted', 'Accepted') :
                         intent.status === 'REJECTED' ? t('helperIntents.rejected', 'Declined') :
                         t('helperIntents.pending', 'Pending')}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('helperIntents.sentAt', 'Invitation date')}：{intent.createdAt ? new Date(intent.createdAt).toLocaleString() : '—'}</p>
                  </div>
                ))
              }
              {helperIntents.length > 0 && (
                <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => navigate('/helper/invitations')}>
                  {t('homeDash.manageInvitations', 'Manage Invitations')}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xl">{t('homeDash.savedJobs', 'Saved jobs')}（{jobPostBookmarks.length}）</CardTitle>
              {jobPostBookmarks.length > 3 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/helper/saved-jobs')}>
                  {t('homeDash.viewMore', '查看更多')}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {jobPostBookmarks.length === 0 ? (
                renderSectionEmpty(t('homeDash.noSavedJobs', '尚未儲存任何工作'))
              ) : (
                jobPostBookmarks.slice(0, 3).map((posting) => (
                  <button
                    key={posting.id}
                    type="button"
                    onClick={() => navigate(`/job-postings/${posting.id}/detail`)}
                    className="flex w-full flex-col gap-1 rounded-2xl border border-border/70 px-4 py-3 text-left transition hover:border-primary/40 hover:bg-primary/5"
                  >
                    <p className="truncate font-medium text-foreground text-sm">{posting.headline || posting.title}</p>
                    <p className="text-xs text-muted-foreground">{formatSalary(posting)}</p>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );

  if (authLoading || loading) {
    return (
      <div className="space-y-4"><Skeleton className="h-8 w-[250px]" /><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>
    );
  }

  return (
    <div className="space-y-6">
      {user?.role === 'EMPLOYER' ? renderEmployerDashboard() : user?.role === 'HELPER' ? renderHelperDashboard() : null}
    </div>
  );
};

export default HomePage;
